import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import * as countriesLib from 'i18n-iso-countries';

import * as deLocale from 'i18n-iso-countries/langs/de.json';
import * as enLocale from 'i18n-iso-countries/langs/en.json';

export interface CountryOption {
  code: string;
  name: string;
}

export type SupportedLanguage = 'de' | 'en';

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private currentLanguage: WritableSignal<SupportedLanguage> = signal<SupportedLanguage>('en');
  private whitelist: WritableSignal<string[]> = signal<string[]>([]);

  /**
   * Reaktive Liste der Länder, die sich automatisch aktualisiert
   * wenn sich Sprache oder Whitelist ändert
   */
  readonly countries: Signal<CountryOption[]> = computed(() => {
    const lang = this.currentLanguage();
    const whitelistCodes = this.whitelist();
    const allCountries = countriesLib.getNames(lang) as Record<string, string>;

    let filtered = Object.entries(allCountries);

    // Filter nach Whitelist, wenn vorhanden
    if (whitelistCodes.length > 0) {
      filtered = filtered.filter(([code]) => whitelistCodes.includes(code));
    }

    return filtered.map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name, lang));
  });

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    countriesLib.registerLocale(deLocale);
    countriesLib.registerLocale(enLocale);

    const initial = this.translate.currentLang ?? this.translate.defaultLang ?? 'en';
    this.setLanguage(initial === 'de' ? 'de' : 'en');

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.setLanguage(event.lang === 'de' ? 'de' : 'en');
    });
  }

  /**
   * Setzt die aktuelle Sprache (löst automatisch Update der Länderliste aus)
   */
  setLanguage(lang: SupportedLanguage): void {
    this.currentLanguage.set(lang);
  }

  /**
   * Gibt die aktuelle Sprache zurück
   */
  getLanguage(): SupportedLanguage {
    return this.currentLanguage();
  }

  /**
   * Setzt die Whitelist der anzuzeigenden Länder
   * @param countryCodes Array von ISO-Ländercodes (z.B. ['DE', 'AT', 'CH'])
   *                     Leeres Array = alle Länder anzeigen
   */
  setWhitelist(countryCodes: string[]): void {
    this.whitelist.set(countryCodes);
  }

  /**
   * Gibt die aktuelle Whitelist zurück
   */
  getWhitelist(): string[] {
    return this.whitelist();
  }
}
